import type { JSX } from "react";
import { useEffect } from "react";

interface Props {
  title: string;
  description: string;
  ogImage?: string;
  canonical?: string;
}

const setOrCreate = (selector: string, attrs: Record<string, string>): void => {
  const existing = document.head.querySelector(selector);
  if (existing) {
    Object.entries(attrs).forEach(([k, v]) => existing.setAttribute(k, v));
    return;
  }
  const el = document.createElement(selector.split("[")[0] ?? "meta");
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  document.head.appendChild(el);
};

const DEFAULT_TITLE = "Adam Kotlorz";

export const Meta = ({
  title,
  description,
  ogImage,
  canonical,
}: Props): JSX.Element => {
  useEffect(() => {
    document.title = title;
    setOrCreate('meta[name="description"]', {
      name: "description",
      content: description,
    });
    if (ogImage) {
      setOrCreate('meta[property="og:image"]', {
        property: "og:image",
        content: ogImage,
      });
    }
    setOrCreate('meta[property="og:title"]', {
      property: "og:title",
      content: title,
    });
    setOrCreate('meta[property="og:description"]', {
      property: "og:description",
      content: description,
    });
    setOrCreate('meta[property="og:type"]', {
      property: "og:type",
      content: "website",
    });
    setOrCreate('meta[property="og:site_name"]', {
      property: "og:site_name",
      content: "Adam Kotlorz",
    });
    setOrCreate('meta[name="robots"]', {
      name: "robots",
      content: "index,follow",
    });
    setOrCreate('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    setOrCreate('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: title,
    });
    setOrCreate('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: description,
    });
    if (ogImage) {
      setOrCreate('meta[name="twitter:image"]', {
        name: "twitter:image",
        content: ogImage,
      });
    }
    if (canonical) {
      setOrCreate('meta[property="og:url"]', {
        property: "og:url",
        content: canonical,
      });
    }
    if (canonical) {
      const link = document.head.querySelector('link[rel="canonical"]');
      if (link) {
        link.setAttribute("href", canonical);
      } else {
        const l = document.createElement("link");
        l.rel = "canonical";
        l.href = canonical;
        document.head.appendChild(l);
      }
    }
    return () => {
      document.title = DEFAULT_TITLE;
    };
  }, [title, description, ogImage, canonical]);
  return <></>;
};
